import AddIcon from "@mui/icons-material/Add";
import { Badge, Button, IconButton, Tooltip } from "@mui/material";
import ReactHtmlParser from "html-react-parser";
import { Link, useSearchParams } from "react-router-dom";
import { ForumOutlined, InfoOutlined, Visibility } from "@mui/icons-material";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import HighlightedText from "components/Shared/HighlightedText";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useMemo } from "react";
import { useLazyGetAnnouncementListQuery } from "store/services/announcementApi";


const AnnouncementListScreen = () => {

  const { isAdmin, isContractor, isCustomer, isVendor, isVendorStaff } = useAuth();

  const [searchParams] = useSearchParams();

  const getAnnouncementResponseTitle = (row) => {
    if (!row?.responses?.length) return "No Responses";

    const _latestResponse = row?.responses?.at(-1);

    return (
      _latestResponse && (
        <>
          <div className="text-sm underline">Last Response: </div>

          <div>{ReactHtmlParser(_latestResponse?.content)}</div>
        </>
      )
    );
  };

  const keyword = searchParams.get("search") || "";

  const basePath = useMemo(() => {
    if (isCustomer) return "/app/customer/announcement";
    if (isVendor) return "/app/vendor/announcement";
    if (isVendorStaff) return "/app/staff/announcement";
    if (isContractor) return "/app/contractor/announcement";
    return "/app/crm/announcement"; // default for admin
  }, [isCustomer, isVendor, isVendorStaff, isContractor]);


  const columns = [
    {
      name: "Title",
      selector: (row) => row?.title,
      minWidth: "300px",
      sortable: true,
      cell: (row) => (
        <>
          {row?.title ? (
            <HighlightedText text={row?.title} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </>
      ),
    },
    {
      name: "Visible To",
      cell: (row) => (
        <div className="flex flex-col gap-2 py-2">
          {row?.visibleTo?.map((text, index) => (
            <li key={index} className="list-disc">
              {text}
            </li>
          )) ?? "N/A"}
        </div>
      ),
    },
    {
      name: "Added User",
      cell: (row) => (
        <div className="flex flex-col gap-2 py-2">
          <Link
            title={row?.addedUser?.name}
            to={`/app/crm/user/${row?.addedUser?._id}/detail`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.addedUser?.name ?? "N/A"}
          </Link>
        </div>
      ),
    },
    {
      name: "Attachment",
      selector: (row) => row?.attachments?.length > 0 ? row?.attachments : [],
      cell: (row) =>
        row?.attachments?.length > 0 && (
          <div className="flex gap-3">
            {
              row?.attachments?.map((attachment) => (
                <a
                  key={attachment?._id}
                  href={attachment?.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "var(--primary)" }}
                >
                  <IconButton
                    size="small"
                    aria-label="View Attachment"
                    onClick={() => window.open(attachment?.url, "_blank")}
                  >
                    < Visibility />
                  </IconButton>
                </a>
              ))}
          </div>
        ),
      minWidth: "50px"
    },
    {
      name: "Created Date",
      selector: (row) => row?.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} dateOnly />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Tooltip title={getAnnouncementResponseTitle(row)} arrow>
            <IconButton size="small">
              <Badge badgeContent={row?.responses?.length} color="primary">
                <ForumOutlined />
              </Badge>
            </IconButton>
          </Tooltip>
          <Link to={`${basePath}/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Announcement">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>
          <Link to={`/app/crm/announcement/${row._id}/edit`}>
            {isAdmin && (<IconButton size="small" aria-label="Edit Announcement">
              <ModeEditOutlineTwoToneIcon />
            </IconButton>)}
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const breadcrumbs = [
    {
      title: "Announcements",
    },
  ];

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  // RTK Query

  const [
    listAnnouncements,
    {
      data: announcementList,
      isLoading: loadingAnnouncements,
      isFetching: fetchingAnnouncements,
      error,
    },
  ] = useLazyGetAnnouncementListQuery();

  React.useEffect(() => {
    listAnnouncements({
      page: currentPage,
      limit: perPage,
      ...(keyword && { search: keyword }),
    });
  }, [currentPage, listAnnouncements, keyword, perPage]);

  useAppMeta("Announcements");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <TableFilter
          searchLabel="Title"
          disabled={loadingAnnouncements || fetchingAnnouncements}
        />
        <AppTable
          columns={columns}
          data={announcementList?.data}
          progressPending={loadingAnnouncements || fetchingAnnouncements}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={announcementList?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default AnnouncementListScreen;

const PageActions = () => {
  const { isVendor } = useAuth()
  return (
    <div
      style={{
        display: "flex",
        alignContent: "center",
        justifyContent: "space-between",
      }}
    >
      {!isVendor && (<Link to="/app/crm/announcement/create">
        <Button startIcon={<AddIcon />} variant="contained">
          New Announcement
        </Button>
      </Link>)}
    </div>
  );
};
